import { _ } from "../../polyglot/polyglot.js";
import { formatAsShortDate } from "../../helpers/date-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import DimensionObjectPicker from "./DimensionObjectPicker.js";
import regionWidget from "../regionWidget.js";

/**
 * `FiscalYearWidget` is a widget to choose among the different options to
 * import dimension objects for a given fiscal year.
 *
 * @param {*} spec.fiscalYear - Fiscal year mockup
 * @param {boolean} [selected] - When true, the fiscal year's checkbox will
 *   be initially checked.
 */
export default class FiscalYearWidget extends Widget {
	constructor({ fiscalYear, selected = false } = {}) {
		super(...arguments);

		this._fiscalYear = fiscalYear;

		this._dimensionTreeLeaves = this._getDimensionTreeLeaves(
			this._fiscalYear.dimensionTree,
		);

		this._objectsWithTransactions = this._dimensionTreeLeaves.filter(
			(object) => object.referenceCount,
		);

		this._selected = selected;
		this._customSelected = [];

		this._selection = () =>
			this._objectsWithTransactions.map((object) => object.id);

		this._customSelectionDialog = new DimensionObjectPicker({
			dimensionObjects: this._dimensionTreeLeaves,
			selection: this._customSelected,
		});

		this._customSelectionDialog.onChange.register(() => {
			this._customSelected = this._customSelectionDialog.getSelected();
			this._customSelectionText.update();
		});

		this._customSelectionText = regionWidget({
			content: (html) => {
				html.render(
					_("{customSelected} selected", {
						customSelected: this._customSelected.length,
					}),
				);
			},
		});
	}

	isSelected() {
		return this._selected;
	}

	getData() {
		return {
			importId: this._fiscalYear.importId,
			selectedDimensions: this._selection(),
		};
	}

	renderContentOn(html) {
		html.div(
			{ klass: "fiscal-year" },
			html.div(
				{ klass: "form" },
				html.label(
					{ klass: "label-bold" },
					html.input({
						type: "checkbox",
						checked: this._selected,
						change: () => {
							this._selected = !this._selected;
						},
					}),
					_("Fiscal Year: {range}", {
						range: `${formatAsShortDate(
							this._fiscalYear.start,
						)} - ${formatAsShortDate(this._fiscalYear.end)}`,
					}),
				),
			),
			html.div(
				{ klass: "form check-group" },
				html.label(
					html.input({
						type: "radio",
						name: this._fiscalYear.start,
						klass: "form-group",
						checked: "checked",
						change: this._selectAllWithTransaction.bind(this),
					}),
					_("Import all objects with transactions ({objects})", {
						objects: this._objectsWithTransactions.length,
					}),
				),
				html.label(
					html.input({
						type: "radio",
						name: this._fiscalYear.start,
						klass: "form-group",
						change: this._selectAll.bind(this),
					}),
					_("Import all objects({objects})", {
						objects: this._dimensionTreeLeaves.length,
					}),
				),
				html.label(
					html.input({
						type: "radio",
						name: this._fiscalYear.start,
						klass: "form-group",
						change: this._selectCustom.bind(this),
					}),
					_("Custom import ({customSelectedLink: ignored})", {
						customSelectedLink: () => (html) => {
							html.a(this._customSelectionText).click(() => {
								this._customSelectionDialog.show();
							});
						},
					}),
				),
			),
		);
	}

	_selectAllWithTransaction() {
		this._selection = () =>
			this._objectsWithTransactions.map((object) => object.id);
	}

	_selectAll() {
		this._selection = () =>
			this._dimensionTreeLeaves.map((object) => object.id);
	}

	_selectCustom() {
		this._selection = () => this._customSelected;
	}

	/**
	 * Return the leaves in a dimension tree.
	 */
	_getDimensionTreeLeaves(dimension) {
		// eslint-disable-next-line no-shadow -- bad practice, we should fix this
		const recurse = (dimension, acc) => {
			if (dimension.children.length === 0) {
				acc.push(dimension);
			} else {
				dimension.children.forEach((child) => {
					recurse(child, acc);
				});
			}
			return acc;
		};

		return recurse(dimension, []);
	}
}
